'use client'

import { type AxiosError } from 'axios'
import { useCallback } from 'react'
import { mutate } from 'swr'
import useSWRInfinite, {
  type SWRInfiniteResponse,
  unstable_serialize,
} from 'swr/infinite'

import {
  type AccountControllerV2ApiAccountV2TenantLanguageOrdersGetRequest,
  AccountControllerV2ApiFactory,
  type SuccessReadUserOrdersV2,
} from '@redteclab/api/clients/bully'

import { useGlooApi } from '../../api'
import { useGlobalConfigContext } from '../../global-config'

const SWR_KEY = 'userOrdersGet'

const getSwrInfiniteKey =
  ({
    itemsPerPage,
    language,
    tenant,
  }: Omit<
    AccountControllerV2ApiAccountV2TenantLanguageOrdersGetRequest,
    'page'
  >) =>
  (
    pageIndex: number,
  ): AccountControllerV2ApiAccountV2TenantLanguageOrdersGetRequest & {
    key: string
  } => ({
    itemsPerPage,
    key: SWR_KEY,
    language,
    page: pageIndex,
    tenant,
  })

export const useApiOrdersSwrGetOrdersInfinite = (
  itemsPerPage: number,
): SWRInfiniteResponse<SuccessReadUserOrdersV2, AxiosError> => {
  const config = useGlobalConfigContext()
  const client = useGlooApi(AccountControllerV2ApiFactory)
  const getKey = getSwrInfiniteKey({
    itemsPerPage,
    language: config.language,
    tenant: config.tenantAndEnv,
  })

  const fetcher = async (
    key: ReturnType<typeof getKey>,
  ): Promise<SuccessReadUserOrdersV2> => {
    const response = await client.accountV2TenantLanguageOrdersGet({
      itemsPerPage: key.itemsPerPage,
      language: key.language,
      page: key.page,
      tenant: key.tenant,
    })

    return response.data
  }

  return useSWRInfinite(getKey, fetcher, {
    revalidateFirstPage: false,
  })
}

export const useRefetchApiOrdersSwrGetOrdersInfinite = (
  itemsPerPage: number,
): (() => Promise<SuccessReadUserOrdersV2[] | undefined>) => {
  const config = useGlobalConfigContext()
  const client = useGlooApi(AccountControllerV2ApiFactory)

  return useCallback(async (): Promise<
    SuccessReadUserOrdersV2[] | undefined
  > => {
    const getKey = getSwrInfiniteKey({
      itemsPerPage,
      language: config.language,
      tenant: config.tenantAndEnv,
    })
    const response = await client.accountV2TenantLanguageOrdersGet({
      itemsPerPage,
      language: config.language,
      page: 0,
      tenant: config.tenantAndEnv,
    })
    const firstPageData = response.data

    await mutate<SuccessReadUserOrdersV2>(getKey(0), firstPageData, {
      revalidate: false,
    })

    return mutate<SuccessReadUserOrdersV2[]>(
      unstable_serialize(getKey),
      [firstPageData],
      { revalidate: false },
    )
  }, [client, config.language, config.tenantAndEnv, itemsPerPage])
}
